/**
 * `/hermes` — Hermes Chat (design-intelligence §2.3).
 *
 * The API surface has been complete since Wave-3 lane S2 and had no client at
 * all; `docs/FOLLOWUPS.md` recorded it as "Hermes Chat has no client. […] the
 * only consumer is the test suite". This is the client.
 *
 * ## Layout
 *
 * Two panes at `md+` (sessions | transcript), ONE at phone width: the session
 * list is the screen when no conversation is open, and opening one replaces it,
 * with a back control. Rendering both panes at 390px would give the transcript
 * about 200px, and a scroll container inside a scroll container is the worst
 * possible mobile chat.
 *
 * ## Send behaviour
 *
 * There is no streaming procedure and no chat SSE frame (see `api/chat.ts`), so
 * a turn is one request. The composer therefore:
 *
 *  - echoes the message optimistically, so the transcript never looks like it
 *    swallowed it;
 *  - keeps the text in state until the turn RESOLVES, and puts it back in the
 *    box if the call fails — a network error must not eat what someone typed;
 *  - disables send while a turn is in flight, because the bounded loop is
 *    per-turn and two concurrent turns on one session would interleave.
 *
 * ## The gate
 *
 * Proposals render as `ProposalCard`s under the transcript and NOTHING here
 * confirms one automatically. The outstanding ones are listed first because they
 * are the only part of the screen that is waiting on the user.
 */

import { Button, Card, cn, EmptyState, SkeletonRows, Textarea, toast } from '@crm/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeftIcon, MessageSquarePlusIcon, SendIcon, SparklesIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useChatActionMutation,
  useChatHistoryQuery,
  useChatSendMutation,
  useChatSessionsQuery,
} from '../../api/chat.ts';
import { ChatTranscript } from '../../components/hermes/chat-transcript.tsx';
import { ProposalCard } from '../../components/hermes/proposal-card.tsx';
import { PageHeader } from '../../components/page.tsx';
import { FeatureBoundary, RouteErrorPanel } from '../../components/route-error-boundary.tsx';
import { useIsMobile } from '../../hooks/useMediaQuery.ts';
import { toClientError } from '../../lib/errors.ts';
import { relativeTime } from '../../lib/format.ts';
import { parseHermesSearch } from '../../lib/search.ts';

export const Route = createFileRoute('/_authed/hermes')({
  validateSearch: parseHermesSearch,
  component: HermesChatPage,
  errorComponent: RouteErrorPanel,
});

const MAX_MESSAGE = 4_000;

function SessionList({
  activeId,
  onOpen,
  onNew,
}: {
  activeId: string | null;
  onOpen: (sessionId: string) => void;
  onNew: () => void;
}) {
  const sessions = useChatSessionsQuery();
  const items = sessions.data?.items ?? [];

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <Button size="sm" className="justify-start" onClick={onNew}>
        <MessageSquarePlusIcon aria-hidden />
        New conversation
      </Button>

      {sessions.isLoading ? (
        <SkeletonRows rows={4} />
      ) : sessions.isError ? (
        <p className="text-sm text-muted-foreground">
          Could not load your conversations. {toClientError(sessions.error).message}
        </p>
      ) : items.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">No conversations yet. Start one above.</p>
      ) : (
        <ul className="flex min-h-0 flex-col gap-1 overflow-y-auto">
          {items.map((session) => (
            <li key={session.id}>
              <button
                type="button"
                onClick={() => onOpen(session.id)}
                aria-current={session.id === activeId ? 'true' : undefined}
                className={cn(
                  'flex w-full min-h-11 flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left',
                  'hover:bg-accent hover:text-accent-foreground',
                  session.id === activeId && 'bg-accent font-medium text-accent-foreground',
                )}
              >
                <span className="line-clamp-2 text-sm">{session.title ?? 'Untitled chat'}</span>
                <span className="text-xs text-muted-foreground">
                  {session.messageCount} message{session.messageCount === 1 ? '' : 's'}
                  {session.lastMessageAt === null
                    ? ''
                    : ` · ${relativeTime(session.lastMessageAt) ?? ''}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Exported for `__tests__/hermes-chat.test.tsx`.
 *
 * The page itself reads `Route.useSearch()`, which needs a registered file route
 * and a router; this pane needs neither, and it owns the two behaviours worth
 * pinning — the optimistic echo, and giving a failed message back to the box.
 */
export function Conversation({
  sessionId,
  onSessionCreated,
  onBack,
  showBack,
}: {
  sessionId: string | null;
  onSessionCreated: (id: string) => void;
  onBack: () => void;
  showBack: boolean;
}) {
  const history = useChatHistoryQuery(sessionId);
  const send = useChatSendMutation();
  const decide = useChatActionMutation();
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const messages = history.data?.messages ?? [];
  const actions = history.data?.actions ?? [];

  // Outstanding first: they are the only part of the screen waiting on a human.
  const outstanding = useMemo(
    () => actions.filter((action) => action.status === 'proposed'),
    [actions],
  );
  const decided = useMemo(
    () => actions.filter((action) => action.status !== 'proposed'),
    [actions],
  );

  // Follow the conversation down as turns land. `scrollIntoView` is stubbed in
  // jsdom by the test setup, hence the optional call.
  //
  // `messageCount` and `pendingSent` are read INSIDE the effect as well as
  // listed as dependencies: the lint rule is right that a body which only calls
  // a ref method does not "use" them, and the two values ARE the trigger, so
  // reading them makes the intent explicit rather than suppressing the rule.
  const messageCount = messages.length;
  const pendingSent = pending !== null;
  useEffect(() => {
    void messageCount;
    void pendingSent;
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messageCount, pendingSent]);

  const submit = () => {
    const message = draft.trim();
    if (message === '' || send.isPending) return;
    setPending(message);
    setDraft('');
    send.mutate(
      { message, ...(sessionId === null ? {} : { sessionId }) },
      {
        onSuccess: (result) => {
          setPending(null);
          if (sessionId === null) {
            onSessionCreated(result.sessionId);
          }
          // A dropped turn is a 200 with a server-written explanation. Toast it
          // as well as rendering it: the transcript may be scrolled away.
          if (result.reply === null && result.message !== null) {
            toast.error('Hermes could not answer that', { description: result.message });
          }
        },
        onError: (error) => {
          setPending(null);
          // Give the words back. Losing a typed message to a flaky network is
          // the one failure a chat UI must never have.
          setDraft(message);
          toast.error('Could not send that message', {
            description: toClientError(error).message,
          });
        },
      },
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {showBack ? (
          <Button variant="ghost" size="sm" className="w-fit" onClick={onBack}>
            <ArrowLeftIcon aria-hidden />
            All conversations
          </Button>
        ) : null}

        {sessionId !== null && history.isLoading ? (
          <SkeletonRows rows={4} />
        ) : sessionId !== null && history.isError ? (
          <EmptyState
            kind="error"
            title="Could not load this conversation"
            description={toClientError(history.error).message}
            action={
              <Button size="sm" variant="outline" onClick={() => void history.refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <ChatTranscript
            messages={messages}
            pendingUserMessage={pending}
            isSending={send.isPending}
          />
        )}

        {outstanding.length === 0 ? null : (
          <section aria-label="Waiting for your approval" className="flex flex-col gap-2">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Waiting for you
            </h2>
            {outstanding.map((action) => (
              <ProposalCard
                key={action.id}
                action={action}
                pending={decide.isPending}
                onDecide={(decision) =>
                  decide.mutate(
                    { actionId: action.id, sessionId: sessionId ?? '', decision },
                    {
                      onError: (error) =>
                        toast.error(
                          decision === 'confirm'
                            ? 'Could not approve that'
                            : 'Could not decline that',
                          { description: toClientError(error).message },
                        ),
                    },
                  )
                }
              />
            ))}
          </section>
        )}

        {decided.length === 0 ? null : (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              {decided.length} earlier decision{decided.length === 1 ? '' : 's'}
            </summary>
            <div className="mt-2 flex flex-col gap-2">
              {decided.map((action) => (
                <ProposalCard key={action.id} action={action} onDecide={() => {}} />
              ))}
            </div>
          </details>
        )}

        <div ref={bottomRef} />
      </div>

      {/* The composer is OUTSIDE the scroll container so it stays reachable with
          one thumb while the transcript scrolls behind it. */}
      <form
        className="flex shrink-0 items-end gap-2 border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, MAX_MESSAGE))}
          aria-label="Message Hermes"
          placeholder="Who should I follow up with this week?"
          rows={2}
          className="min-h-11 flex-1 resize-none"
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter breaks the line — the convention every
            // chat surface shares. A phone keyboard's Return inserts a newline
            // (no `metaKey`), which is why the send button is always visible.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button
          type="submit"
          size="icon"
          aria-label="Send"
          disabled={draft.trim() === '' || send.isPending}
        >
          <SendIcon aria-hidden />
        </Button>
      </form>
    </div>
  );
}

function HermesChatPage() {
  const { session } = Route.useSearch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const sessionId = session ?? null;

  /**
   * "New conversation" on a PHONE, where the two panes are one screen.
   *
   * A brand-new chat has no id — the server assigns one when the first turn
   * lands — so `?session=` cannot represent it, and on a phone "no session"
   * already means "show the list". Without this flag, tapping New on a phone
   * left the user on the list with no composer: the one interaction the screen
   * exists for. It is local state rather than a URL value because an unsent,
   * unaddressed chat is not a thing worth linking to.
   */
  const [composingNew, setComposingNew] = useState(false);

  const open = (id: string | null) => {
    setComposingNew(false);
    void navigate({
      to: '/hermes',
      search: id === null ? {} : { session: id },
      replace: false,
    });
  };

  const startNew = () => {
    setComposingNew(true);
    if (sessionId !== null) {
      void navigate({ to: '/hermes', search: {}, replace: false });
    }
  };

  // On a phone the two panes are one screen at a time; on a desktop both show.
  const showList = !isMobile || (sessionId === null && !composingNew);
  const showConversation = !isMobile || sessionId !== null || composingNew;

  return (
    <>
      <PageHeader
        title="Hermes"
        description="Ask about your network. Hermes reads your CRM, and asks before it writes anything."
      />
      <div className="flex min-h-0 flex-1 md:flex-row">
        {showList ? (
          <div
            className={cn(
              'flex min-h-0 flex-col gap-2 p-3',
              'md:w-64 md:shrink-0 md:border-r md:border-border',
              isMobile ? 'flex-1' : '',
            )}
          >
            <FeatureBoundary feature="your conversations">
              <SessionList activeId={sessionId} onOpen={open} onNew={startNew} />
            </FeatureBoundary>
            {isMobile && sessionId === null ? (
              <EmptyState
                className="mt-4"
                icon={<SparklesIcon aria-hidden />}
                title="Ask Hermes about your network"
                description="Start a conversation above. Hermes never sends a message or changes a record without asking you first."
              />
            ) : null}
          </div>
        ) : null}

        {showConversation ? (
          <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-none border-0 p-0 md:rounded-lg">
            <FeatureBoundary feature="this conversation">
              <Conversation
                sessionId={sessionId}
                onSessionCreated={(id) => open(id)}
                onBack={() => open(null)}
                showBack={isMobile}
              />
            </FeatureBoundary>
          </Card>
        ) : null}
      </div>
    </>
  );
}
